import {
  AddressElement,
  CardElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import { Spinner } from "@via/components/loading/loading";
import { classNames } from "@via/utils/helpers";
import StripeBadge from "@via/pages/billing/stripe-badge.tsx";
import {
  useBillingInfo,
  useUpdateBillingInfo,
} from "@via/pages/billing/billing.api";
import { BillingInfoResponseBody } from "common";

interface UpdateBillingInfoFormProps {
  email: string;
  clientSecret: string;
  isDisabled: boolean;
  onEdit: () => void;
}

const UpdateBillingInfoForm: React.FC<UpdateBillingInfoFormProps> = ({
  email,
  clientSecret,
  isDisabled,
  onEdit,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const {
    data: billingInfo,
    isLoading: isBillingInfoLoading,
    isError: isBillingInfoError,
    error: billingInfoError,
  } = useBillingInfo();

  const isBillingInfoNotFound =
    isBillingInfoError && (billingInfoError as any)?.response?.status === 404;

  const { mutateAsync: updateBilling, isPending: isUpdateBillingLoading } =
    useUpdateBillingInfo();

  const [addressValue, setAddressValue] = useState<any>(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const [cardError, setCardError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCardError(null);
    setAddressError(null);

    if (!stripe || !elements || !clientSecret) return;

    const cardElement = elements.getElement(CardElement);
    const addressElement = elements.getElement(AddressElement);

    let hasError = false;

    if (!addressElement || !addressComplete) {
      setAddressError("Please complete your billing address.");
      hasError = true;
    }

    if (!cardElement || !cardComplete) {
      setCardError("Please complete your card details.");
      hasError = true;
    }

    if (hasError) return;

    setSubmitting(true);

    const { setupIntent, error: confirmError } = await stripe.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card: cardElement!,
          billing_details: {
            email,
            address: addressValue?.address,
            name: addressValue?.name || "",
            phone: addressValue?.phone || undefined,
          },
        },
      }
    );

    if (confirmError || !setupIntent?.payment_method) {
      setCardError(confirmError?.message || "Something went wrong.");
      setSubmitting(false);
      return;
    }

    await updateBilling({
      paymentMethodId: setupIntent.payment_method as string,
    });

    setSubmitting(false);

    onEdit();
  };

  const address: BillingInfoResponseBody["billing"]["address"] = billingInfo
    ?.billing?.address ?? {
    line1: null,
    line2: null,
    postal_code: null,
    city: null,
    state: null,
    country: null,
  };

  const fullAddress = [
    address.line1,
    address.line2,
    address.postal_code,
    address.city,
    address.state,
    address.country,
  ]
    .filter(part => part != null && part !== "")
    .join(", ");

  return (
    <>
      {isBillingInfoLoading ? (
        <div className="grid grid-cols-2 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-2">
          <SkeletonLoader count={4} />
        </div>
      ) : isBillingInfoError && !isBillingInfoNotFound ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {isDisabled ? (
            isBillingInfoNotFound ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center">
                <p className="text-sm text-gray-500">
                  No billing information on file. Click{" "}
                  <span className="font-medium text-gray-700">Edit</span> to add
                  a payment method.
                </p>
              </div>
            ) : (
              <>
                <div className="mt-2 flex gap-4">
                  <div className="w-1/2">
                    <label className="text-sm font-medium text-gray-700">
                      Cardholder Name
                    </label>
                    <input
                      value={billingInfo?.billing?.name || ""}
                      disabled
                      className="w-full mt-2 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
                    />
                  </div>

                  <div className="w-1/2">
                    <label className="text-sm font-medium text-gray-700">
                      Credit Card
                    </label>
                    <input
                      value={
                        billingInfo?.card?.last4
                          ? `**** **** **** ${billingInfo.card.last4}`
                          : "**** **** **** ****"
                      }
                      disabled
                      className="w-full mt-2 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
                    />
                  </div>
                </div>

                <div className="mt-2 w-full">
                  <label className="text-sm font-medium text-gray-700">
                    Billing Address
                  </label>
                  <textarea
                    value={fullAddress}
                    disabled
                    rows={2}
                    className="w-full mt-2 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
                  />
                </div>

                {billingInfo?.billing?.phone && (
                  <div className="mt-2 w-full">
                    <label className="text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      value={billingInfo.billing.phone}
                      disabled
                      className="w-full mt-2 rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-800 shadow-sm"
                    />
                  </div>
                )}
              </>
            )
          ) : (
            <>
              {/* Address */}
              <div className="mt-2">
                <label className="text-sm font-medium text-gray-700">
                  Billing Address
                </label>
                <div
                  className={classNames(
                    "mt-2 mb-2 rounded-md border px-3 py-3 shadow-sm",
                    addressError
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300 bg-white"
                  )}>
                  <AddressElement
                    options={{
                      mode: "billing",
                      fields: { phone: "always" },
                      defaultValues: {
                        name: billingInfo?.billing?.name || "",
                        phone: billingInfo?.billing?.phone || "",
                        address: {
                          line1: billingInfo?.billing?.address?.line1 || "",
                          line2: billingInfo?.billing?.address?.line2 || "",
                          city: billingInfo?.billing?.address?.city || "",
                          state: billingInfo?.billing?.address?.state || "",
                          postal_code:
                            billingInfo?.billing?.address?.postal_code || "",
                          country:
                            billingInfo?.billing?.address?.country || "US", // fallback
                        },
                      },
                    }}
                    onChange={(e: StripeAddressElementChangeEvent) => {
                      setAddressValue(e.value);
                      setAddressComplete(e.complete);
                      setAddressError(null);
                    }}
                  />
                </div>
                {addressError && (
                  <div className="text-red-600 text-sm mt-1">
                    {addressError}
                  </div>
                )}
              </div>

              {/* Card */}
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Card Info
                </label>
                <div
                  className={classNames(
                    "mt-2 rounded-md px-3 py-3 shadow-sm",
                    cardError
                      ? "border border-red-500 bg-red-50"
                      : "border border-gray-300 bg-white focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500"
                  )}>
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#111827",
                          fontFamily: "inherit",
                          "::placeholder": { color: "#9CA3AF" },
                        },
                        invalid: {
                          color: "#EF4444",
                        },
                      },
                      iconStyle: "solid",
                    }}
                    onChange={event => {
                      setCardComplete(event.complete);
                      setCardError(event.error?.message || null);
                    }}
                  />
                </div>
                {cardError && (
                  <div className="text-red-600 text-sm mt-1">{cardError}</div>
                )}
              </div>
            </>
          )}

          {/* Stripe badge */}
          <StripeBadge />

          {/* Submit */}
          <div className="flex flex-shrink-0 justify-end">
            {isDisabled ? (
              <button
                type="button"
                disabled={!stripe || submitting || isUpdateBillingLoading}
                onClick={onEdit}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  disabled={!stripe || submitting || isUpdateBillingLoading}
                  onClick={onEdit}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-2 mr-2">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!stripe || submitting || isUpdateBillingLoading}
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
                  <Spinner loading={submitting || isUpdateBillingLoading}>
                    Update
                  </Spinner>
                </button>
              </>
            )}
          </div>
        </form>
      )}
    </>
  );
};

export default UpdateBillingInfoForm;
